class ProductInquiryButton extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Store product data
    this._productData = {
      id: '',
      variantId: '',
      url: '',
      title: '',
      image: ''
    };

    // Local storage key format
    this._storageKeyFormat = 'inquiry_product_{id}';

    // Initial state
    this._isModalOpen = false;

    this._isLoading = false;
  }

  // Called when element is connected to DOM
  connectedCallback() {
    this._productData = {
      id: this.getAttribute('product-id') || '',
      variantId: this.getAttribute('variant-id') || '',
      url: this.getAttribute('product-url') || window.location.pathname,
      title: this.getAttribute('product-title') || document.title,
      image: this.getAttribute('product-image') || ''
    };

    this.render();
    this.setupEventListeners();

    requestAnimationFrame(() => {
      this._updateRefererUrl();
    });

    // Check form status after a slight delay to ensure the form is fully rendered
    setTimeout(() => {
      this.checkFormStatus();
    }, 100);

    const filterForm = document.querySelectorAll('collections-filter-form');

    if (filterForm) {
      filterForm.forEach((form) => {
        form.addEventListener('filter-form-change', this._updateRefererUrl.bind(this));
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();

    const filterForm = document.querySelectorAll('collections-filter-form');

    if (filterForm) {
      filterForm.forEach((form) => {
        form.removeEventListener('filter-form-change', this._updateRefererUrl.bind(this));
      });
    }
  }

  // Custom attributes to watch
  static get observedAttributes() {
    return [
      'product-id',
      'variant-id',
      'product-url',
      'product-title',
      'product-image',
      'button-text'
    ];
  }

  // Handle attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (name) {
      case 'product-id':
        this._productData.id = newValue;
        break;
      case 'variant-id':
        this._productData.variantId = newValue;

        this._updateFormItemValue('input[name="contact[variant_id]"', newValue);

        break;
      case 'product-url':
        this._productData.url = newValue;
        break;
      case 'product-title':
        this._productData.title = newValue;
        if (this.shadowRoot.querySelector('.product-title')) {
          this.shadowRoot.querySelector('.product-title').textContent = newValue;
        }
        break;
      case 'product-image':
        this._productData.image = newValue;
        if (this.shadowRoot.querySelector('.product-image')) {
          this.shadowRoot.querySelector('.product-image').src = newValue;
        }
        break;
      case 'button-text':
        if (this.shadowRoot.querySelector('.inquiry-button')) {
          this.shadowRoot.querySelector('.inquiry-button').textContent = newValue;
        }
        break;
    }
  }

  _updateRefererUrl() {
    const { origin, href, pathname, search } = window.location;

    this._updateFormItemValue('input[name="contact[referrer_url]"', href);
    this._updateFormItemValue('input[name="return_to"]', `${origin}${pathname}${search}`);
  }

  _updateFormItemValue(selector, value) {
    const form = this.getForm();
    if (form) {
      const input = form.querySelector(selector);
      if (input) input.value = value;
    }
  }

  // Get storage key based on product ID
  _getStorageKey() {
    return this._storageKeyFormat
      .replace('{id}', this._productData.id || 'default');
  }

  // Store submission state before form submission
  storeSubmissionState() {
    const state = {
      submitted: true,
      productId: this._productData.id,
      variantId: this._productData.variantId,
      timestamp: new Date().getTime()
    };

    localStorage.setItem(this._getStorageKey(), JSON.stringify(state));
  }

  // Get submission state from localStorage
  getSubmissionState() {
    try {
      const stateStr = localStorage.getItem(this._getStorageKey());
      if (stateStr) {
        return JSON.parse(stateStr);
      }
    } catch (e) {
      console.error('Error parsing submission state', e);
    }
    return null;
  }

  // Clear submission state
  clearSubmissionState() {
    localStorage.removeItem(this._getStorageKey());
  }

  // Check if form has status messages and validate against current product
  checkFormStatus() {
    const form = this.getForm();
    if (!form) return;

    this.hideLoadingIndicator();

    // Check if there are any success or error messages
    const statusMessage = form.querySelector('.shopify-form-status');
    if (!statusMessage) return;

    // Get stored submission state
    const submissionState = this.getSubmissionState();

    // If status message exists but no matching submission state exists for this product,
    // or there's a mismatch with the product ID, remove the message
    if (!submissionState || submissionState.productId !== this._productData.id) {
      console.log('Removing mismatched status message for product:', this._productData.id);
      this.clearFormMessages(form);
      return;
    }

    // If we have a matching submission state and status message, open the modal
    if (statusMessage.classList.contains('success') || statusMessage.classList.contains('error')) {
      console.log('Found matching status for product:', this._productData.id);
      this.openFormModal();

      // Clear the submission state as it's now been handled
      this.clearSubmissionState();
    }
  }

  // Render the component
  render() {
    const buttonText = this.getAttribute('button-text') || 'Inquiry';

    this.shadowRoot.innerHTML = `
      <style>
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        /* Button styles - can be customized via CSS variables */
        .inquiry-button {
          background-color: var(--inquiry-button-bg, #5c3197);
          color: var(--inquiry-button-color, white);
          border: var(--inquiry-button-border, none);
          border-radius: var(--inquiry-button-radius, .5625rem);
          padding: var(--inquiry-button-padding, 0 .625rem);
          font-size: var(--inquiry-button-font-size, .875rem);
          font-weight: var(--inquiry-button-font-weight, 500);
          font-family: var(--inquiry-button-font-family, inherit);
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          box-shadow: var(--inquiry-button-shadow, 0 1px 3px rgba(0,0,0,0.1));
          outline: none;
          margin: var(--inquiry-button-margin, 0);
          width: var(--inquiry-button-width, 100%);
          height: var(--inquiry-button-height, 2rem);
          line-height: var(--inquiry-button-height, 2rem);
          white-space: nowrap;
        }

        .inquiry-button:hover {
          background-color: var(--inquiry-button-hover-bg, #4a2370);
          transform: var(--inquiry-button-hover-transform, translateY(-1px));
          box-shadow: var(--inquiry-button-hover-shadow, 0 4px 8px rgba(0,0,0,0.15));
        }

        .inquiry-button:active {
          transform: var(--inquiry-button-active-transform, translateY(0));
          box-shadow: var(--inquiry-button-active-shadow, 0 1px 2px rgba(0,0,0,0.1));
        }

        /* Modal styles */
        .modal-overlay {
          display: flex;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0,0,0,0.45);
          z-index: 9999;
          align-items: center;
          justify-content: center;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.25s ease, visibility 0s linear 0.25s;
          padding: 20px;
        }

        .modal-overlay.visible {
          opacity: 1;
          visibility: visible;
          transition: opacity 0.25s ease, visibility 0s linear 0s;
        }

        .modal {
          display: flex;
          flex-direction: column;
          position: relative;
          background-color: white;
          width: 100%;
          max-width: 550px;
          max-height: 90vh;
          overflow-y: auto;
          border-radius: 0.5rem;
          padding: 0;
          transform: scale(0.95) translateY(10px);
          opacity: 0;
          box-shadow: 0 0.625rem 1.875rem rgba(0,0,0,0.2);
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
          overflow: hidden;
        }

        .modal-overlay.visible .modal {
          transform: scale(1) translateY(0);
          opacity: 1;
        }

        .modal-header {
          padding: 16px 24px;
          border-bottom: 1px solid #eaeaea;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin: 0;
          max-width: calc(100% - 30px);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .close-btn {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: transparent;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
          position: relative;
        }

        .close-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .close-btn:not(:disabled):hover {
          background-color: #f2f2f2;
          transform: rotate(90deg);
        }

        .close-btn::before, .close-btn::after {
          content: '';
          position: absolute;
          width: 14px;
          height: 2px;
          background-color: #888;
          transition: background-color 0.2s;
        }

        .close-btn:not(:disabled):hover::before, .close-btn:not(:disabled):hover::after {
          background-color: #555;
        }

        .close-btn::before {
          transform: rotate(45deg);
        }

        .close-btn::after {
          transform: rotate(-45deg);
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
        }

        /* Product info styles */
        .product-info {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
        }

        .product-image-container {
          width: 60px;
          height: 60px;
          border-radius: 4px;
          overflow: hidden;
          margin-right: 16px;
          flex-shrink: 0;
          border: 1px solid #eaeaea;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-title {
          font-size: 16px;
          color: #333;
          margin: 0;
          font-weight: 500;
          line-height: 1.4;
          text-align: left;
        }

        /* Form slot styling */
        ::slotted(form) {
          display: block;
          width: 100%;
        }

        .loading-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f2f2f2;
          border-radius: 50%;
          border-top-color: #666;
          margin-right: 10px;
          -webkit-animation: spin 1s linear infinite;
          animation: spin 1s linear infinite;
        }

        @-webkit-keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Test buttons container */
        .test-buttons {
          display: flex;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px dashed #ddd;
        }

        .test-button {
          padding: 10px 16px;
          margin-right: 10px;
          border-radius: 4px;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .test-button.success {
          background-color: #4caf50;
          color: white;
        }

        .test-button.error {
          background-color: #f44336;
          color: white;
        }

        .test-button:not([disabled]):hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .test-button:not([disabled]):active {
          transform: translateY(0);
        }

        .test-button[disabled] {
          background-color: #b3b3b3;
          color: #f2f2f2;
          cursor: not-allowed;
          opacity: 0.5;
        }

        /* Ripple effect for buttons */
        .inquiry-button .ripple {
          position: absolute;
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.6s linear;
          background-color: rgba(255, 255, 255, 0.3);
        }

        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }

        /* Animation keyframes */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>

      <!-- Custom button with slot for flexible content -->
      <slot name="button">
        <button class="inquiry-button">${buttonText}</button>
      </slot>

      <!-- Modal overlay for inquiry form -->
      <div class="modal-overlay" id="formModal">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">${this._productData.title}</h3>
            <button class="close-btn" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <div class="product-info">
              <div class="product-image-container">
                <img class="product-image" src="${this._productData.image}" alt="Product Image">
              </div>
              <h4 class="product-title">${this._productData.title}</h4>
            </div>

            <!-- Slot for the Shopify form -->
            <slot name="form"></slot>

            <!-- Test buttons container -->
            <!-- <div class="test-buttons" id="testButtons">
              <button class="test-button success" id="testSuccessBtn">Test Success Response</button>
              <button class="test-button error" id="testErrorBtn">Test Error Response</button>
            </div> -->
          </div>
        </div>
      </div>
    `;
  }

  // Setup all event listeners
  setupEventListeners() {
    // Get DOM elements
    const button = this.shadowRoot.querySelector('.inquiry-button');
    const slot = this.shadowRoot.querySelector('slot[name="button"]');
    const formModal = this.shadowRoot.querySelector('#formModal');
    const closeButtons = this.shadowRoot.querySelectorAll('.close-btn');
    const testSuccessBtn = this.shadowRoot.querySelector('#testSuccessBtn');
    const testErrorBtn = this.shadowRoot.querySelector('#testErrorBtn');

    // Button click to open modal
    if (button) {
      button.addEventListener('click', this.openFormModal.bind(this));
    }

    // Handle slotted button
    if (slot) {
      slot.addEventListener('slotchange', () => {
        const assignedElements = slot.assignedElements();
        if (assignedElements.length > 0) {
          assignedElements[0].addEventListener('click', this.openFormModal.bind(this));
        }
      });
    }

    // Close buttons
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeModal();
      });
    });

    // Click outside modal to close
    formModal.addEventListener('click', (e) => {
      if (e.target === formModal) {
        this.closeModal();
      }
    });

    // ESC key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isModalOpen) {
        this.closeModal();
      }
    });

    // Handle form submission
    const formSlot = this.shadowRoot.querySelector('slot[name="form"]');
    formSlot.addEventListener('slotchange', () => {
      const assignedElements = formSlot.assignedElements();
      if (assignedElements.length > 0) {
        assignedElements[0].addEventListener('submit', this.handleFormSubmit.bind(this));
      }
    });

    // Test success button
    if (testSuccessBtn) {
      testSuccessBtn.addEventListener('click', this.testSuccessSubmission.bind(this));
    }

    // Test error button
    if (testErrorBtn) {
      testErrorBtn.addEventListener('click', this.testErrorSubmission.bind(this));
    }

    // Ripple effect for buttons
    if (button) {
      button.addEventListener('click', this.createRipple.bind(this));
    }
  }

  removeEventListeners() {
    const button = this.shadowRoot.querySelector('.inquiry-button');
    const slot = this.shadowRoot.querySelector('slot[name="button"]');
    const formModal = this.shadowRoot.querySelector('#formModal');
    const closeButtons = this.shadowRoot.querySelectorAll('.close-btn');
    const testSuccessBtn = this.shadowRoot.querySelector('#testSuccessBtn');
    const testErrorBtn = this.shadowRoot.querySelector('#testErrorBtn');

    if (button) {
      button.removeEventListener('click', this.openFormModal.bind(this));
    }

    if (slot) {
      slot.removeEventListener('slotchange', () => {
        const assignedElements = slot.assignedElements();
        if (assignedElements.length > 0) {
          assignedElements[0].removeEventListener('click', this.openFormModal.bind(this));
        }
      });
    }

    closeButtons.forEach(btn => {
      btn.removeEventListener('click', () => {
        this.closeModal();
      });
    });

    formModal.removeEventListener('click', (e) => {
      if (e.target === formModal) {
        this.closeModal();
      }
    });

    document.removeEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isModalOpen) {
        this.closeModal();
      }
    });

    const formSlot = this.shadowRoot.querySelector('slot[name="form"]');
    formSlot.removeEventListener('slotchange', () => {
      const assignedElements = formSlot.assignedElements();
      if (assignedElements.length > 0) {
        assignedElements[0].removeEventListener('submit', this.handleFormSubmit.bind(this));
      }
    });

    if (testSuccessBtn) {
      testSuccessBtn.removeEventListener('click', this.testSuccessSubmission.bind(this));
    }

    if (testErrorBtn) {
      testErrorBtn.removeEventListener('click', this.testErrorSubmission.bind(this));
    }

    if (button) {
      button.removeEventListener('click', this.createRipple.bind(this));
    }
  }

  // Ripple effect for buttons
  createRipple(e) {
    const button = e.currentTarget;
    const circle = document.createElement('span');

    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    const rect = button.getBoundingClientRect();

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${e.clientX - rect.left - radius}px`;
    circle.style.top = `${e.clientY - rect.top - radius}px`;
    circle.classList.add('ripple');

    const ripple = button.getElementsByClassName('ripple')[0];
    if (ripple) {
      ripple.remove();
    }

    button.appendChild(circle);
  }

  // Open form modal
  openFormModal(e) {
    if (e) e.preventDefault();

    // Show form modal
    const formModal = this.shadowRoot.querySelector('#formModal');
    formModal.classList.add('visible');

    this._isModalOpen = true;

    // Disable body scroll
    document.body.style.overflow = 'hidden';

    // Dispatch modal opened event
    this.dispatchEvent(new CustomEvent('modal-opened', {
      bubbles: true
    }));
  }

  // Close modal
  closeModal(force = false) {
    if (!force && this._isLoading) return;

    const formModal = this.shadowRoot.querySelector('#formModal');

    // Apply closing animation
    const modal = formModal.querySelector('.modal');
    modal.style.transform = 'scale(0.95) translateY(10px)';
    modal.style.opacity = '0';

    // Wait for animation to complete before hiding
    setTimeout(() => {
      const form = this.getForm();

      if (form) {
        // Clear any existing messages
        this.clearFormMessages(form);
      }

      formModal.classList.remove('visible');

      // Reset animation styles
      modal.style.transform = '';
      modal.style.opacity = '';

      this._isModalOpen = false;

      // Re-enable body scroll
      document.body.style.overflow = '';

      // Dispatch modal closed event
      this.dispatchEvent(new CustomEvent('modal-closed', {
        bubbles: true
      }));
    }, 200);
  }

  // Handle form submission
  handleFormSubmit(e) {
    this.showLoadingIndicator();

    // Store product ID and variant ID
    this.storeSubmissionState();

    // Dispatch form submit event
    this.dispatchEvent(new CustomEvent('inquiry-submitting', {
      detail: {
        productId: this._productData.id,
        variantId: this._productData.variantId,
        productTitle: this._productData.title
      },
      bubbles: true
    }));

    // Let the form submit and page refresh happen naturally
  }

  getForm() {
    const formSlot = this.shadowRoot.querySelector('slot[name="form"]');
    if (!formSlot) return null;

    const forms = formSlot.assignedElements();

    if (forms.length > 0) {
      return forms[0];
    }

    return null;
  }

  // Test success submission
  testSuccessSubmission(e) {
    // Prevent any default behavior
    if (e) e.preventDefault();

    this.showLoadingIndicator();

    // Store product ID and variant ID
    this.storeSubmissionState();

    // Close modal first
    this.closeModal(true);

    // Wait for processing (simulating server request)
    setTimeout(() => {
      const form = this.getForm();

      if (form) {
        // Create success message
        const successMessage = document.createElement('div');
        successMessage.className = 'shopify-form-status success';
        successMessage.textContent = 'Thank you for your inquiry! We will get back to you soon.';

        // Insert at beginning of form
        form.prepend(successMessage);

        // Reset form fields
        form.reset();

        // Now open modal to show the success message
        // this.openFormModal();
        this.checkFormStatus();

        console.log('Test: Success response simulated');
      }
    }, 1500); // 1.5s delay to simulate network request
  }

  // Test error submission
  testErrorSubmission(e) {
    // Prevent any default behavior
    if (e) e.preventDefault();

    this.showLoadingIndicator();

    // Store product ID and variant ID
    this.storeSubmissionState();

    // Close modal first
    this.closeModal(true);

    // Wait for processing (simulating server request)
    setTimeout(() => {
      const form = this.getForm();

      if (form) {
        // Create error message
        const errorMessage = document.createElement('div');
        errorMessage.className = 'shopify-form-status error';
        errorMessage.innerHTML = `
          <p>There was a problem with your submission. Please check the following:</p>
          <ul>
            <li>Email address appears to be invalid</li>
            <li>Quantity must be greater than zero</li>
          </ul>
        `;

        // Insert at beginning of form
        form.prepend(errorMessage);

        // Add field-specific errors
        /* this.addFieldError(form, '[name="contact[email]"]', 'Please enter a valid email address');
        this.addFieldError(form, '[name="contact[quantity]"]', 'Please enter a quantity greater than zero'); */

        // Now open modal to show the error message
        // this.openFormModal();
        this.checkFormStatus();

        console.log('Test: Error response simulated');
      }
    }, 1500); // 1.5s delay to simulate network request
  }

  // Add field error with improved unit label handling
  addFieldError(form, selector, errorMessage) {
    const field = form.querySelector(selector);
    if (field) {
      // Add error class to field
      field.classList.add('error');

      // Handle unit-label container specially
      const unitContainer = field.closest('.input-with-unit');
      if (unitContainer) {
        // Make sure the unit container also gets the position-relative class if needed
        unitContainer.style.position = 'relative';

        // Find the unit label and make sure it stays visible
        const unitLabel = unitContainer.querySelector('.unit-label');
        if (unitLabel) {
          unitLabel.style.position = 'absolute';
          unitLabel.style.top = '1px';
          unitLabel.style.right = '1px';
          unitLabel.style.bottom = '1px';
          unitLabel.style.zIndex = '2';
          unitLabel.style.display = 'flex';
        }
      }

      // Create error message element
      const errorElement = document.createElement('div');
      errorElement.className = 'field-error';
      errorElement.textContent = errorMessage;

      // Add error message after the field or its container
      if (unitContainer) {
        unitContainer.parentNode.appendChild(errorElement);
      } else {
        field.parentNode.appendChild(errorElement);
      }
    }
  }

  // Clear form messages
  clearFormMessages(form) {
    if (!form) return;

    // Remove status messages
    const statusMessages = form.querySelectorAll('.shopify-form-status');
    statusMessages.forEach(msg => msg.remove());

    // Remove field errors
    const fieldErrors = form.querySelectorAll('.field-error');
    fieldErrors.forEach(el => el.remove());

    // Reset error styling on inputs
    const errorFields = form.querySelectorAll('.error');
    errorFields.forEach(field => {
      field.classList.remove('error');
      field.style.borderColor = '';
    });
  }

  createLoadingIndicator() {
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'loading-indicator';
    loadingIndicator.id = 'loadingIndicator';

    const spinner = document.createElement('div');
    spinner.className = 'spinner';

    const message = document.createElement('div');
    message.className = 'message';
    message.textContent = 'Loading...';

    loadingIndicator.appendChild(spinner);
    loadingIndicator.appendChild(message);

    return loadingIndicator;
  }

  showLoadingIndicator() {
    const form = this.getForm();
    if (!form) return;

    this.hideLoadingIndicator();

    const buttons = [form.querySelector('button[type="submit"]'), this.shadowRoot.querySelector('#testSuccessBtn'), this.shadowRoot.querySelector('#testErrorBtn')].filter(Boolean);

    if (buttons && buttons.length > 0) {
      buttons.forEach(button => {
        button.setAttribute('data-previous-value', button.textContent);
        button.textContent = '';

        const loadingIndicator = this.createLoadingIndicator();

        button.appendChild(loadingIndicator);

        button.disabled = true;
      });

      this._isLoading = true;
    }

    const closeBtn = this.shadowRoot.querySelector('.close-btn');

    if (closeBtn) {
      closeBtn.disabled = true;
    }
  }

  hideLoadingIndicator() {
    const form = this.getForm();
    if (!form) return;

    const loadingIndicators = [...form.querySelectorAll('.loading-indicator'), ...this.shadowRoot.querySelectorAll('.loading-indicator')];

    if (loadingIndicators && loadingIndicators.length > 0) {
      loadingIndicators.forEach(loadingIndicator => {
        loadingIndicator.remove();
      });
    }

    const buttons = [form.querySelector('button[type="submit"]'), this.shadowRoot.querySelector('#testSuccessBtn'), this.shadowRoot.querySelector('#testErrorBtn')].filter(Boolean);

    if (buttons && buttons.length > 0) {
      buttons.forEach(button => {
        const previousValue = button.getAttribute('data-previous-value');
        if (previousValue) {
          button.textContent = previousValue;
          button.removeAttribute('data-previous-value');
        }

        button.disabled = false;
      });

      this._isLoading = false;
    }

    const closeBtn = this.shadowRoot.querySelector('.close-btn');

    if (closeBtn) {
      closeBtn.disabled = false;
    }
  }
}

// Register custom element
customElements.define('product-inquiry-button', ProductInquiryButton);